import moment from 'moment';
import 'moment-precise-range-plugin';
import StoreModuleService from '../StoreModuleService';
import { MODULE_NAME } from './const';
import { arrayToObject } from '@/utils/helpers';
import { ADMIN_ROLE } from '@/utils/account-service.const';

const buildAccountGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations
export const accounts = buildAccountGetterObject('accounts');
export const userAccounts = buildAccountGetterObject('userAccounts');
export const invitedAccounts = buildAccountGetterObject('invitedAccounts');
export const joinedAccounts = buildAccountGetterObject('joinedAccounts');
export const account = buildAccountGetterObject('account');
export const activeAccountLink = buildAccountGetterObject('activeAccountLink');
export const activeAccountId = buildAccountGetterObject('activeAccountId');
export const loadingMembers = buildAccountGetterObject('loadingMembers');
export const teamMembers = buildAccountGetterObject('teamMembers');
export const configs = buildAccountGetterObject('configs');
export const cnames = buildAccountGetterObject('cnames');
export const flattenCnames = buildAccountGetterObject('flattenCnames');
export const isAccountOwner = buildAccountGetterObject('isAccountOwner');
export const accountChannelConfigs = buildAccountGetterObject('accountChannelConfigs');
export const hasAccountPermission = buildAccountGetterObject('hasAccountPermission');
export const teamMemberByUserId = buildAccountGetterObject('teamMemberByUserId');
export const credByServiceVersionId = buildAccountGetterObject('credByServiceVersionId');
export const useAccountLink = buildAccountGetterObject('useAccountLink');
export const useAccount = buildAccountGetterObject('useAccount');
export const isUserAccountOwner = buildAccountGetterObject('isUserAccountOwner');
export const dataLifetimeHumanReadable = buildAccountGetterObject('dataLifetimeHumanReadable');
export const useIsSmartUpgradeEnabled = buildAccountGetterObject('useIsSmartUpgradeEnabled');
export const useAccountAdmins = buildAccountGetterObject('useAccountAdmins');

const getters = {
	[userAccounts.getter]: (state) => state.userAccounts,
	[useIsSmartUpgradeEnabled.getter]: (state, getters) => getters[account.getter]?.is_smart_upgrade_enabled,
	[activeAccountId.getter]: (state) => state.activeAccountId,
	[invitedAccounts.getter]: (state, getters) =>
		getters[userAccounts.getter].filter((accountLink) => accountLink.is_invitation_pending).map(({ account }) => account),
	[joinedAccounts.getter]: (state, getters) =>
		getters[userAccounts.getter].filter((accountLink) => !accountLink.is_invitation_pending).map(({ account }) => account),
	[accounts.getter]: (state, getters) => arrayToObject(getters[userAccounts.getter].map((accountLink) => accountLink.account)),
	[useAccountLink.getter]: (state, getters) => (accountId) =>
		getters[userAccounts.getter].find((accountLink) => accountLink.account.id === accountId),
	[activeAccountLink.getter]: (state, getters) => getters[useAccountLink.getter](getters[activeAccountId.getter]),
	[useAccount.getter]: (state, getters) => (accountId) => getters[useAccountLink.getter](accountId)?.account,
	[account.getter]: (state, getters) => getters[activeAccountLink.getter]?.account,
	[loadingMembers.getter]: (state) => state.loadingMembers,
	[teamMembers.getter]: (state) => state.members,
	[useAccountAdmins.getter]: (state, getters) => getters[teamMembers.getter].filter(({ role }) => ADMIN_ROLE === role),
	[teamMemberByUserId.getter]: (state, getters) => (userId) => getters[teamMembers.getter].find(({ user_id }) => user_id === userId),
	[configs.getter]: (state, getters) => (getters[account.getter] ? getters[account.getter].configs : {}),
	[cnames.getter]: (state, getters) => getters[configs.getter].cnames || {},
	[flattenCnames.getter]: (state, getters) => {
		const flattenCnames = [];
		for (const [serviceId, cnamesList] of Object.entries(getters[cnames.getter])) {
			flattenCnames.push(...cnamesList.map((cname) => ({ cname, serviceId: parseInt(serviceId) })));
		}
		return flattenCnames;
	},
	[isUserAccountOwner.getter]: (state, getters) => (accountId, userId) => getters[useAccount.getter](accountId)?.owner?.id === userId,
	[isAccountOwner.getter]: (state, getters) => (userId) => getters[isUserAccountOwner.getter](getters[activeAccountId.getter], userId),
	[accountChannelConfigs.getter]: (state, getters) => arrayToObject(getters[account.getter].channel_configs || [], 'channel'),
	[hasAccountPermission.getter]: (state, getters) => (permission) => getters[account.getter]?.permissions.includes(permission),
	[credByServiceVersionId.getter]: (state, getters) => (svId) =>
		getters[account.getter]?.notification_channel_credentials_data?.find(({ service_version_id }) => service_version_id === svId),
	[dataLifetimeHumanReadable.getter]: (state, getters) => {
		const now = moment();
		const then = now.clone().add(getters[account.getter].data_lifetime, 'seconds');
		return moment.preciseDiff(now, then);
	},
};

export default getters;
